import { useEffect, useState, useMemo, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import client, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Lock, ShieldCheck, Eye, EyeOff, AlertTriangle, Download, Calendar, ChevronRight, ChevronDown } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { hoursToHM } from "@/lib/duration";
import { ClockOutNoteIcon } from "@/components/ClockOutNoteIcon";

const PAY_STATUS = {
  paid: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  partially_paid: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  unpaid: "bg-red-500/15 text-red-300 border-red-500/30",
  discrepancy: "bg-purple-500/15 text-purple-300 border-purple-500/30",
};

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function PayrollVault() {
  const [rates, setRates] = useState([]);
  const [payments, setPayments] = useState([]);
  const [calcs, setCalcs] = useState([]);
  const [pending, setPending] = useState({ pendingShifts: 0, pendingHours: 0 });
  const [reveal, setReveal] = useState(false);
  const [month, setMonth] = useState(currentMonth());
  const [expanded, setExpanded] = useState(new Set());
  const { t } = useI18n();
  const navigate = useNavigate();

  const months = useMemo(() => {
    const arr = [];
    const d = new Date();
    for (let i = 0; i < 12; i++) {
      const dd = new Date(d.getFullYear(), d.getMonth() - i, 1);
      arr.push({
        val: `${dd.getFullYear()}-${String(dd.getMonth() + 1).padStart(2, "0")}`,
        label: dd.toLocaleString("en-GB", { month: "long", year: "numeric" }),
      });
    }
    return arr;
  }, []);

  const monthLabel = useMemo(
    () => (month === "all" ? "" : (months.find((m) => m.val === month)?.label || month)),
    [month, months]
  );

  useEffect(() => {
    client.get("/payroll/rates").then((r) => setRates(r.data)).catch((e) => toast.error(apiErr(e.response?.data?.detail)));
    client.get("/payroll/payments").then((r) => setPayments(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const qp = month === "all" ? "" : `?month=${month}`;
    client.get(`/payroll/calculations${qp}`).then((r) => setCalcs(r.data)).catch(() => setCalcs([]));
    client.get(`/payroll/pending${qp}`).then((r) => setPending(r.data)).catch(() => setPending({ pendingShifts: 0, pendingHours: 0 }));
  }, [month]);

  const money = (v) => (reveal ? `£${Number(v).toFixed(2)}` : "£•••");

  const toggle = (id) => setExpanded((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const exportCsv = async () => {
    if (month === "all") return;
    try {
      const res = await client.get(`/payroll/export?month=${month}&format=csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `payroll_${month}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="payroll-vault-page">
      {/* Obsidian header banner */}
      <Card className="vault-gradient border-0 text-white p-6 relative overflow-hidden">
        <div className="absolute -right-6 -top-6 opacity-10"><Lock className="h-40 w-40" /></div>
        <div className="relative flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
              <Lock className="h-6 w-6 text-amber-400" />
            </div>
            <div>
              <h2 className="font-display text-2xl font-extrabold">{t("payroll.heading", "Payroll Vault")}</h2>
              <p className="text-slate-300 text-sm flex items-center gap-2 mt-0.5">
                <ShieldCheck className="h-4 w-4 text-teal-400" /> {t("payroll.subtitle", "Owner-only · confidential · encrypted at rest")}
              </p>
            </div>
          </div>
          <Button data-testid="payroll-confidential-lock-toggle" onClick={() => setReveal((v) => !v)}
            variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20">
            {reveal ? <><EyeOff className="h-4 w-4 mr-2" /> {t("payroll.hide", "Hide amounts")}</> : <><Eye className="h-4 w-4 mr-2" /> {t("payroll.reveal", "Reveal amounts")}</>}
          </Button>
        </div>
      </Card>

      {/* Pending-hours approval banner */}
      {pending.pendingHours > 0 && (
        <Card data-testid="payroll-pending-banner" className="border-amber-300 bg-amber-50 p-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
            <div className="text-sm text-amber-800">
              <b data-testid="payroll-pending-figure">{pending.pendingShifts} shift{pending.pendingShifts === 1 ? "" : "s"}{pending.pendingFieldDays ? ` + ${pending.pendingFieldDays} field timesheet day${pending.pendingFieldDays === 1 ? "" : "s"}` : ""} ({hoursToHM(pending.pendingHours)})</b>{" "}
              awaiting approval{monthLabel ? ` for ${monthLabel}` : ""}. These are <b>not</b> included in the totals below until approved.
            </div>
          </div>
          {pending.pendingFieldDays > 0 && <Button variant="outline" onClick={() => navigate("/timesheets")} data-testid="payroll-approve-timesheets-cta" className="border-amber-400 text-amber-800 shrink-0">{t("payroll.reviewTimesheets", "Approve timesheets")}</Button>}
          <Button onClick={() => navigate("/attendance")} data-testid="payroll-approve-cta" className="bg-amber-600 hover:bg-amber-700 text-white shrink-0">
            {t("payroll.reviewApprove", "Review & approve")}
          </Button>
        </Card>
      )}

      <Tabs defaultValue="calculations">
        <TabsList>
          <TabsTrigger value="calculations" data-testid="payroll-tab-calculations">{t("payroll.tabCalc", "Calculations")}</TabsTrigger>
          <TabsTrigger value="rates" data-testid="payroll-tab-rates">{t("payroll.tabRates", "Rates")}</TabsTrigger>
          <TabsTrigger value="payments" data-testid="payroll-tab-payments">{t("payroll.tabPayments", "Payments")}</TabsTrigger>
        </TabsList>

        <TabsContent value="calculations">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-slate-400" />
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger data-testid="payroll-month-picker" className="h-9 w-[190px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("payroll.allTime", "All time")}</SelectItem>
                  {months.map((m) => <SelectItem key={m.val} value={m.val}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" data-testid="payroll-export-btn" onClick={exportCsv} disabled={month === "all"}>
              <Download className="h-4 w-4 mr-2" />{t("payroll.exportCsv", "Export CSV")}
            </Button>
          </div>
          <Card className="border-slate-200">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>{t("payroll.thWorker", "Worker")}</TableHead>
                <TableHead>{t("payroll.thHours", "Approved hrs")}</TableHead>
                <TableHead className="text-right">{t("payroll.thOwed", "Amount owed")}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {calcs.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-8">{t("payroll.noHours", "No approved hours to calculate for this period.")}</TableCell></TableRow> :
                  calcs.map((c) => (
                    <Fragment key={c.workerId}>
                      <TableRow data-testid={`calc-row-${c.workerId}`}>
                        <TableCell>
                          <button onClick={() => toggle(c.workerId)} data-testid={`calc-expand-${c.workerId}`} className="text-slate-400 hover:text-slate-700 transition-colors">
                            {expanded.has(c.workerId) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </button>
                        </TableCell>
                        <TableCell className="font-medium">{c.workerName}<ClockOutNoteIcon notes={c.clockOutNotes} testid={`pay-notes-${c.workerId}`} /></TableCell>
                        <TableCell>{hoursToHM(c.hours)}</TableCell>
                        <TableCell className="text-right font-mono font-semibold">{money(c.amountOwed)}</TableCell>
                      </TableRow>
                      {expanded.has(c.workerId) && (
                        <TableRow className="bg-slate-50" data-testid={`calc-breakdown-${c.workerId}`}>
                          <TableCell></TableCell>
                          <TableCell colSpan={3}>
                            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-600 py-1">
                              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-sky-500" />{t("payroll.bdSite", "Static sites")}: <b>{hoursToHM(c.siteHours)}</b></span>
                              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-violet-500" />{t("payroll.bdField", "Field (jobs + General)")}: <b>{hoursToHM(c.fieldHours)}</b></span>
                              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-500" />{t("payroll.bdUnsch", "Unscheduled")}: <b>{hoursToHM(c.unscheduledHours)}</b></span>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="rates">
          <Card className="border-slate-200">
            <Table>
              <TableHeader><TableRow><TableHead>{t("payroll.thWorker", "Worker")}</TableHead><TableHead>{t("payroll.thRate", "Rate / hr")}</TableHead><TableHead>{t("payroll.thEff", "Effective")}</TableHead></TableRow></TableHeader>
              <TableBody>
                {rates.map((r) => (
                  <TableRow key={r.id} data-testid={`rate-row-${r.id}`}>
                    <TableCell className="font-medium">{r.workerName}</TableCell>
                    <TableCell className="font-mono">{money(r.rate)}</TableCell>
                    <TableCell>{r.effectiveDate}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="payments">
          <Card className="border-slate-200">
            <Table>
              <TableHeader><TableRow><TableHead>{t("payroll.thWorker", "Worker")}</TableHead><TableHead>{t("payroll.thPeriod", "Period")}</TableHead><TableHead>{t("payroll.thOwed", "Owed")}</TableHead><TableHead>{t("payroll.thPaid", "Paid")}</TableHead><TableHead>{t("payroll.thMethod", "Method")}</TableHead><TableHead>{t("common.status", "Status")}</TableHead></TableRow></TableHeader>
              <TableBody>
                {payments.map((p) => (
                  <TableRow key={p.id} data-testid={`payment-row-${p.id}`}>
                    <TableCell className="font-medium">{p.workerName}</TableCell>
                    <TableCell>{p.payPeriod}</TableCell>
                    <TableCell className="font-mono">{money(p.amountOwed)}</TableCell>
                    <TableCell className="font-mono">{money(p.amountPaid)}</TableCell>
                    <TableCell className="text-slate-500 text-sm">{reveal ? p.paymentMethod : "•••"}</TableCell>
                    <TableCell><Badge variant="outline" className={`${PAY_STATUS[p.status]} bg-slate-900`}>{p.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
